import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Row,
} from 'reactstrap';
import { useEffect, useMemo, useState } from 'react';
import { CourseStateStatus } from '../../../../../types/Courses';
import {
  AdditionalMaterial,
  CourseCategory,
  CourseLesson,
  CourseLessonEditData,
} from '../../../../../types/Tables';
import { useTranslation } from 'react-i18next';
import SimpleModal from '../../../../uiKits/modal/basicModal/SimpleModal';

import AddAdditionalMaterialForm from '../../../../courses/details/[id]/AddAdditionalMaterialForm';
import { hideSwalLoader, showSwalLoader } from '../../../../bonusUi/sweetAlert/Loader';
import { title } from 'process';
import { baseLang } from '../../../../../lib/utils';

type EditProps = {
  courseId: number;
  name: string;
  courseStateStatus: CourseStateStatus;
  close: () => void;
  categories?: CourseCategory[];
  takenPositions: number[];
  editLesson: CourseLesson; // required in edit mode
  updateHandler: (patch: Partial<CourseLessonEditData>) => Promise<void>;
};

type FormFieldValue = string | number | FileList | undefined | null;

const EditLessonForm: React.FC<EditProps> = (props) => {
  const { t, i18n } = useTranslation();
  const pageLang = baseLang(i18n.language);
  // ---- 1) Build defaultValues from editLesson ----
  const defaultValues = useMemo(() => {
    const tr = (lang: 'uk' | 'ru') => props.editLesson.translations?.find((x) => x.lang === lang);

    return {
      // titles
      title: props.editLesson.title ?? '',
      title_uk: tr('uk')?.title ?? '',
      title_ru: tr('ru')?.title ?? '',
      // descriptions
      description: props.editLesson.description ?? '',
      description_uk: tr('uk')?.description ?? '',
      description_ru: tr('ru')?.description ?? '',
      // video meta
      filename: props.editLesson.video?.filename ?? '',
      link: props.editLesson.video?.link ?? '',
      // lesson cover file: starts undefined (user may upload new)
      lessonImage: undefined as FileList | undefined,
      // position (keep as string for input)
      lesson_pos:
        typeof props.editLesson.lesson_pos === 'number'
          ? String(props.editLesson.lesson_pos)
          : (props.editLesson.lesson_pos ?? ''),
      // category defaults for the select
      category: props.editLesson.category ?? { id: 0, name: '' },
      // state
      courseState: props.courseStateStatus,
      additionalMaterials: props.editLesson.additionalMaterials,
    };
  }, [props.editLesson, props.courseStateStatus]);

  // ---- 2) Local UI state ----
  const [uploading, setUploading] = useState(false);
  const [image, setImage] = useState<File | null>(null); // cover image as File for PATCH (optional)
  const [additionals, setAdditionals] = useState<AdditionalMaterial[]>([]);
  const [addMaterialsOpen, setAddMaterialsOpen] = useState(false);

  // category & course state are simple controlled selects
  const [category, setCategory] = useState<{ id: number; name: string }>(defaultValues.category);
  const [courseState, setCourseState] = useState<CourseStateStatus>(defaultValues.courseState);

  // ---- 3) Single source of truth for form inputs ----
  const [formData, setFormData] = useState<Record<string, FormFieldValue>>({
    title: defaultValues.title,
    title_uk: defaultValues.title_uk,
    title_ru: defaultValues.title_ru,
    description: defaultValues.description,
    description_uk: defaultValues.description_uk,
    description_ru: defaultValues.description_ru,
    filename: defaultValues.filename,
    link: defaultValues.link,
    lessonImage: defaultValues.lessonImage,
    lesson_pos: defaultValues.lesson_pos,
  });

  // If editLesson changes (rare), refresh defaults into form

  useEffect(() => {
    setFormData({
      title: defaultValues.title,
      title_uk: defaultValues.title_uk,
      title_ru: defaultValues.title_ru,
      description: defaultValues.description,
      description_uk: defaultValues.description_uk,
      description_ru: defaultValues.description_ru,
      filename: defaultValues.filename,
      link: defaultValues.link,
      lessonImage: defaultValues.lessonImage,
      lesson_pos: defaultValues.lesson_pos,
    });
    setCategory(defaultValues.category);
    setCourseState(defaultValues.courseState);
    setAdditionals(defaultValues.additionalMaterials ?? []);
  }, [defaultValues]);
  const setField = (name: keyof typeof formData, value: FormFieldValue) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const deleteAdditionalMaterial = (material: AdditionalMaterial) => {
    setAdditionals((prev) => prev.filter((el) => el.id !== material.id));
  };

  const selectCategoryTranslation = (cat: CourseCategory) => {
    if (pageLang === 'en') return cat.name;
    if (!cat.translations || cat.translations.length === 0) return cat.name;
    const tr = cat.translations.find((el) => el.lang === pageLang);
    return tr?.name ?? cat.name;
  };

  // ---- 4) Submit (build patch from formData + local state) ----
  const onEditSubmit = async () => {
    showSwalLoader('Loading lesson update');
    const lessonCoverFile =
      formData.lessonImage instanceof FileList && formData.lessonImage.length > 0
        ? formData.lessonImage[0]
        : undefined;

    const patch: Partial<CourseLessonEditData> = {
      id: props.editLesson.id,
      // base
      title: (formData.title as string) ?? '',
      description: (formData.description as string) ?? '',
      // translations flattened (adjust to your DTO if it expects a different shape)
      title_uk: (formData.title_uk as string) ?? '',
      title_ru: (formData.title_ru as string) ?? '',
      description_uk: (formData.description_uk as string) ?? '',
      description_ru: (formData.description_ru as string) ?? '',
      // cover
      lessonCover: lessonCoverFile ?? undefined,
      // video
      videoId: props.editLesson.videoId,
      video: {
        id: props.editLesson.video?.id ?? 0,
        lessonId: props.editLesson.video?.lessonId ?? props.editLesson.id,
        threadId: props.editLesson.video?.threadId ?? '',
        uploadedAt: props.editLesson.video?.uploadedAt ?? '',
        length: props.editLesson.video?.length ?? 0,
        filename: (formData.filename as string) ?? '',
        link: (formData.link as string) ?? '',
      },
      // position & category/state
      lesson_pos: (formData.lesson_pos as string) ?? '',
      category: category?.id || props.editLesson.category.id,
      courseStateStatus: courseState,
      // optionally send additionals if API supports it
      additionalMaterials: additionals,
    };
    setUploading(true);
    try {
      await props.updateHandler(patch);
    } finally {
      setUploading(false);
      hideSwalLoader();
      props.close();
    }
  };

  return (
    <>
      <div className="upload-file-modal-content">
        <h2 className="text-lg font-semibold mb-4">{t('courses.lessons.editLessonFormTitle')}</h2>

        <Row>
          {/* Course parameters */}
          <Col sm="6">
            <Card className="p-3">
              {/* <CardBody> */}
              <CardTitle tag="h5" className="mb-4">
                {t('courses.lessons.lessonForm.courseParams')}
              </CardTitle>

              <Label className="d-flex flex-column">
                {t('courses.generalCourseTitle')}
                {/* <Input type="number" name="courseId" value={props.courseId} disabled hidden /> */}
                <Input type="hidden" name="courseId" value={String(props.courseId)} />
                <Input type="text" name="name" value={props.name} disabled />
              </Label>

              <Label className="w-full d-flex flex-column">
                {t('courses.lessons.lessonForm.adjustCourseState')}
                <Input
                  type="select"
                  name="courseStateStatus"
                  value={courseState}
                  onChange={(e) => setCourseState(e.target.value as CourseStateStatus)}
                >
                  {Object.values(CourseStateStatus).map((status) => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </Input>
              </Label>

              <Label className="w-full d-flex flex-column">
                {t('courses.lessons.lessonForm.courseCategories')}
                <Input
                  type="select"
                  name="categories"
                  value={String(category?.id) ?? '0'}
                  disabled={uploading}
                  onChange={(e) => {
                    const selectedId = Number(e.target.value);
                    const selectedCategory = props.categories?.find(
                      (cat) => Number(cat.id) === selectedId,
                    );
                    if (selectedCategory) {
                      setCategory({
                        id: Number(selectedCategory.id),
                        name: selectedCategory.name ?? '',
                      });
                    } else {
                      setCategory({ id: 0, name: '' });
                    }
                  }}
                >
                  <option value="">{t('courses.lessons.selectCatrgory')}</option>
                  {props.categories?.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {selectCategoryTranslation(cat)}
                    </option>
                  ))}
                </Input>
              </Label>
              {/* </CardBody> */}
            </Card>
          </Col>

          {/* Lesson names */}
          <Col sm="6">
            <Card className="p-3">
              {/* <CardBody> */}
              <CardTitle tag="h5" className="mb-4">
                {t('courses.lessons.lessonForm.lessonNameFormSection')}
              </CardTitle>

              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.lessonName')}
                <Input
                  type="text"
                  value={(formData.title as string) ?? ''}
                  disabled={uploading}
                  onChange={(e) => setField('title', e.target.value)}
                />
              </Label>

              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.lessonName_uk')}
                <Input
                  type="text"
                  value={(formData.title_uk as string) ?? ''}
                  disabled={uploading}
                  onChange={(e) => setField('title_uk', e.target.value)}
                />
              </Label>

              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.lessonName_ru')}
                <Input
                  type="text"
                  value={(formData.title_ru as string) ?? ''}
                  disabled={uploading}
                  onChange={(e) => setField('title_ru', e.target.value)}
                />
              </Label>
              {/* </CardBody> */}
            </Card>
          </Col>

          {/* Descriptions + cover + position */}
          <Col sm="6">
            <Card className="p-3">
              {/* <CardBody> */}
              <CardTitle tag="h5" className="mb-4">
                {t('courses.lessons.lessonForm.lessonDescriptionFormSection')}
              </CardTitle>

              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.lessonDescription')}
                <Input
                  type="text"
                  value={(formData.description as string) ?? ''}
                  disabled={uploading}
                  onChange={(e) => setField('description', e.target.value)}
                />
              </Label>

              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.lessonDescription_uk')}
                <Input
                  type="text"
                  value={(formData.description_uk as string) ?? ''}
                  disabled={uploading}
                  onChange={(e) => setField('description_uk', e.target.value)}
                />
              </Label>

              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.lessonDescription_ru')}
                <Input
                  type="text"
                  value={(formData.description_ru as string) ?? ''}
                  disabled={uploading}
                  onChange={(e) => setField('description_ru', e.target.value)}
                />
              </Label>

              <Label className="d-flex flex-column">
                {t('courses.lessons.lessonForm.addLessonCover')}
                <Input
                  type="file"
                  accept="image/*"
                  disabled={uploading}
                  onChange={(e) => {
                    const files = e.target.files ?? undefined;
                    setField('lessonImage', files);
                    setImage(files && files.length > 0 ? files[0] : null);
                  }}
                  className="mb-4"
                />
              </Label>

              <Label className="d-flex flex-column">
                {t('courses.lessons.inputs.lessonPosition')}
                <Input
                  type="number"
                  name="lesson_pos"
                  value={(formData.lesson_pos as string) ?? ''}
                  disabled={uploading}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setField('lesson_pos', e.target.value)
                  }
                />
              </Label>
              {/* </CardBody> */}
            </Card>
          </Col>

          {/* Additional materials */}
          <Col sm="6">
            <Card className="p-3">
              {/* <CardBody> */}
              <CardTitle tag="h5" className="mb-4">
                {t('courses.lessons.lessonForm.lessonAdditionalsFormSection')}
              </CardTitle>

              <Col sm="12">
                <Button onClick={() => setAddMaterialsOpen(true)} className="mb-3">
                  {t('courses.lessons.lessonForm.addMaterials')}
                </Button>
              </Col>

              {additionals?.length > 0 && (
                <ListGroup className="mt-3">
                  {additionals.map((item) => {
                    const materialsTitleEn = item.title;
                    const translatedTitle = i18n.language === 'uk' ? item.title_uk : item.title_ru;
                    return (
                      <ListGroupItem
                        key={item.id}
                        // className="d-flex justify-content-between align-items-center mb-0 gap-2"
                      >
                        <Col sm="12" className='d-flex justify-content-between align-items-center mb-0'>
                          <p className="m-0">
                            <strong>
                              {i18n.language === 'en' ? materialsTitleEn : translatedTitle}
                            </strong>{' '}
                            {item.kind ? `(${item.kind})` : ''}
                          </p>

                          <div className="d-flex gap-2 align-items-center">
                            {item.url && (
                              <a
                                href={
                                  item.kind === 'file'
                                    ? `${process.env.REACT_APP_BACKEND_STATIC}${item.url}`
                                    : item.url
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {t('courses.lessons.additionalMaterials.view')}
                              </a>
                            )}
                            <Button
                              color="danger"
                              outline
                              onClick={() => deleteAdditionalMaterial(item)}
                            >
                              <i className="icon-trash m-0" />
                            </Button>
                          </div>
                        </Col>
                      </ListGroupItem>
                    );
                  })}
                </ListGroup>
              )}
              {/* </CardBody> */}
            </Card>
          </Col>
        </Row>

        {/* Submit */}
        <Button
          variant="primary"
          className={`mt-4 ${uploading ? 'cursor-not-allowed' : ''} bg-green-200 hover:bg-green-300`}
          onClick={(e) => {
            e.preventDefault();
            onEditSubmit();
          }}
        >
          {uploading ? t('global.saving') : t('global.save')}
        </Button>
      </div>

      {/* Add materials modal */}
      <SimpleModal
        isOpen={addMaterialsOpen}
        close={() => setAddMaterialsOpen(false)}
        size="lg"
        centered
      >
        <AddAdditionalMaterialForm
          addMaterial={(material) => {
            setAdditionals((prev) => [...prev, material]);
            setAddMaterialsOpen(false);
          }}
          close={() => setAddMaterialsOpen(false)}
        />
      </SimpleModal>
    </>
  );
};

export default EditLessonForm;
